<script lang="ts">
	import favicon from '$lib/assets/favicon.svg';
	import type { LayoutData } from './$types';
	import { toMeta, toOpenGraph, toOrganizationJsonLd } from '$lib/utils/seo';
	import { getPrerenderOriginForBrand } from '$lib/brand/registry';
	import Header from '$lib/components/Header.svelte';
	import Footer from '$lib/components/Footer.svelte';
	import ScrollToTop from '$lib/components/ScrollToTop.svelte';
	import { initializeThemeManager } from '$lib/stores/theme-manager';
	import { theme } from '$lib/stores/theme';
	import { onMount } from 'svelte';
	import { afterNavigate } from '$app/navigation';
	import { registerPageViewTrackingWithStore, trackPageView, type PageEvent } from '$lib/ads/metaPixel';
	import { pageEvent } from '$lib/stores/pageEvents';
	import { headerVisible, isLandingPage } from '$lib/stores/headerVisibility';
	import { page } from '$app/state';

	let { children, data }: { children: any, data: LayoutData } = $props();

	// Force reactivity by using $effect to track store changes
	let computedPaddingTop = $state('74px'); // Default to normal padding
	let isLandingPageClass = $state(''); // Track if we're on landing page for CSS

	$effect(() => {
		const currentPath = page.url.pathname;
		const isLanding = currentPath === '/landing/';

		// Use store values if available, otherwise fall back to direct path check
		const effectiveIsLanding = $isLandingPage !== undefined ? $isLandingPage : isLanding;
		const effectiveHeaderVisible = $headerVisible !== undefined ? $headerVisible : true;

		// Set CSS class for landing page
		isLandingPageClass = effectiveIsLanding ? 'landing-page' : '';

		if (!effectiveIsLanding || effectiveHeaderVisible) {
			// Normal padding when not on landing page or header is visible
			// Use CSS custom properties for responsive padding (74px desktop, 100px mobile)
			computedPaddingTop = 'var(--header-padding-top, 74px)';
		} else {
			// No padding when on landing page and header is hidden
			computedPaddingTop = '0px';
		}
	});

	// Initialize theme manager with brand config
	onMount(() => {
		initializeThemeManager(data.config);
		// Theme manager handles initial theme application

		// Register Meta Pixel pageview tracking (includes loading and initialization)
		if (data.config.analytics?.metaPixelId) {
			console.log('Meta Pixel: Initializing with pixel ID:', data.config.analytics.metaPixelId);
		}

		// Register a single afterNavigate handler that reads current store value
		if (data.config.analytics?.metaPixelId) {
			registerPageViewTrackingWithStore(afterNavigate, true, data.config.analytics.metaPixelId, () => $pageEvent);
		}
	});

	// Debug logging to help verify brand loading
	$effect(() => {
		console.log('Brand loaded:', data.brand);
		console.log('Brand config:', data.config);
	});

	// Generate meta tags from brand config
	const meta = $derived(toMeta(data.config));
	const canonicalOrigin = $derived(
		getPrerenderOriginForBrand(data.brand) ?? page.url.origin
	);
	const canonicalHref = $derived(`${canonicalOrigin}${page.url.pathname}`);
	const openGraph = $derived(toOpenGraph(data.config, canonicalHref));
	const organizationJsonLd = $derived(
		toOrganizationJsonLd(data.config, canonicalOrigin + '/')
	);
</script>

<svelte:head>
	<link rel="icon" href={data.config.favicon || favicon} />
	<link rel="canonical" href={canonicalHref} />
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />

	<!-- Open Graph meta tags -->
	<meta property="og:title" content={openGraph['og:title']} />
	<meta property="og:url" content={openGraph['og:url'] ?? canonicalHref} />
	<meta property="og:description" content={openGraph['og:description']} />
	<meta property="og:image" content={openGraph['og:image']} />
	<meta property="og:site_name" content={openGraph['og:site_name']} />
	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:title" content={meta.title} />
	<meta name="twitter:description" content={meta.description} />

	<svelte:element this={'script'} type="application/ld+json">{JSON.stringify(
		organizationJsonLd
	)}</svelte:element>

	{#if data.config.analytics?.plausibleDomain}
		<script defer data-domain={data.config.analytics.plausibleDomain} src="https://plausible.io/js/script.outbound-links.tagged-events.js"></script>
		<script>
			// Define shim before Plausible loads
			window.plausible = window.plausible || function () {
				(window.plausible.q = window.plausible.q || []).push(arguments);
			};
		</script>
	{/if}

	{#if data.config.analytics?.metaPixelId}
		<!-- Meta Pixel noscript fallback -->
		<noscript>
			<img height="1" width="1" style="display:none" alt=""
				 src={"https://www.facebook.com/tr?id=" + data.config.analytics.metaPixelId + "&ev=PageView&noscript=1"} />
		</noscript>
	{/if}

	{#if data.config.analytics?.facebookDomainVerification}
		<!-- Facebook Domain Verification -->
		<meta name="facebook-domain-verification" content={data.config.analytics.facebookDomainVerification} />
	{/if}
</svelte:head>

<style lang="scss">
	@use "$lib/scss/_themes.scss" as *;

	/* Remove body margin for full-bleed design */
	:global(body) {
		margin: 0 !important;
	}

	/* Define responsive header padding as CSS custom properties */
	:root {
		--header-padding-top: 74px; /* Desktop header height + padding */
	}

	@media (max-width: 640px) {
		:root {
			--header-padding-top: 100px; /* Mobile header height + padding (stacked) */
		}
	}

	/* Dynamic main padding - handled via inline styles */
	main {
		transition: padding-top 0.3s ease;
	}

	/* No transition for landing page to prevent initial animation */
	:global(.landing-page main) {
		transition: none !important;
	}

	/* Also target override-landing for 100xdevelopers brand */
	:global(main:has(.override-landing)) {
		transition: none !important;
	}

	/* Mobile responsive padding - handled via inline styles */
	@media (max-width: 640px) {
		main {
			transition: padding-top 0.3s ease;
		}

		:global(.landing-page main) {
			transition: none !important;
		}

		:global(main:has(.override-landing)) {
			transition: none !important;
		}
	}
</style>

<div
	data-brand={data.brand}
	data-config-loaded="true"
	class={isLandingPageClass}
	style="
		--brand-primary: {data.config.primaryColor};
		--brand-secondary: {data.config.secondaryColor};
		background-color: var(--color--page-background, var(--brand-primary));
		color: var(--color--text, white);
		min-height: 100vh;
		display: flex;
		flex-direction: column;
	"
>

	<Header config={data.config} />

	<main style="flex: 1; padding-top: {computedPaddingTop};">
		{@render children?.()}
	</main>

	<Footer config={data.config} />
	<ScrollToTop />
</div>